import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Icons } from '../common';

const QCUploadPage = () => {
  const navigate = useNavigate();
  const dropZoneRef = useRef(null);
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [journal, setJournal] = useState('');
  const [journalAutoDetected, setJournalAutoDetected] = useState(false);
  const [journals, setJournals] = useState([]);
  const [journalSearch, setJournalSearch] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  // Load all journals from backend on mount
  useEffect(() => {
    axios.get('/api/qc/journals')
      .then(res => {
        const sorted = res.data
          .filter(j => j.code !== 'DEFAULT')
          .sort((a, b) => a.code.localeCompare(b.code));
        setJournals(sorted);
      })
      .catch(() => {
        setJournals([
          { code: 'AIAN', name: 'Annals of Indian Academy of Neurology' },
          { code: 'IJA', name: 'Indian Journal of Anaesthesia' },
          { code: 'IJEM', name: 'Indian Journal of Endocrinology and Metabolism' },
          { code: 'IJO', name: 'Indian Journal of Ophthalmology' },
          { code: 'JPGM', name: 'Journal of Postgraduate Medicine' },
        ]);
      });
  }, []);

  // Detect journal code from filename prefix (e.g. "ija_1725_25.docx" → "IJA")
  const detectJournalFromFilename = (filename, journalList) => {
    const prefix = filename.split('_')[0].toUpperCase();
    return journalList.find(j => j.code === prefix) ? prefix : null;
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const applyFile = (selectedFile) => {
    setFile(selectedFile);
    setError(null);
    const detected = detectJournalFromFilename(selectedFile.name, journals);
    if (detected) {
      setJournal(detected);
      setJournalSearch('');
      setJournalAutoDetected(true);
    } else {
      setJournalAutoDetected(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const selectedFile = files[0];
      if (selectedFile.name.endsWith('.docx')) {
        applyFile(selectedFile);
      } else {
        setError('Please upload a .docx file');
      }
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.docx')) {
      applyFile(selectedFile);
    } else {
      setError('Please upload a .docx file');
    }
  };

  const handleAnalyze = async () => {
    if (!file || !journal) {
      setError('Please select both file and journal');
      return;
    }

    setIsAnalyzing(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('journalCode', journal);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 30;
        });
      }, 300);

      const response = await axios.post('/api/qc/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      clearInterval(progressInterval);
      setProgress(100);

      // Redirect to analysis report
      setTimeout(() => {
        navigate(`/qc/analysis/${response.data.analysisId}`);
      }, 500);
    } catch (err) {
      setError(err.response?.data?.error || 'Analysis failed. Please try again.');
      setIsAnalyzing(false);
      setProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50 to-blue-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate('/qc/dashboard')}
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium mb-4 transition-colors"
          >
            <Icons.ChevronLeftIcon className="w-5 h-5" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Upload Document for QC Analysis</h1>
          <p className="text-gray-600 mt-2">Select a Word document and journal to begin validation</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">

        {/* Step Indicator */}
        <div className="mb-12 flex items-center justify-between">
          {[
            { step: 1, label: 'Upload File', active: !isAnalyzing },
            { step: 2, label: 'Select Journal', active: !!file && !isAnalyzing },
            { step: 3, label: 'Analyzing', active: isAnalyzing },
          ].map((item, idx) => (
            <div key={idx} className="flex-1 flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all duration-300 ${
                item.active
                  ? 'bg-gradient-to-br from-indigo-600 to-blue-600 text-white shadow-lg shadow-indigo-500/50'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {item.step}
              </div>
              <span className={`ml-3 font-medium transition-colors duration-300 ${
                item.active ? 'text-gray-900' : 'text-gray-500'
              }`}>
                {item.label}
              </span>
              {idx < 2 && (
                <div className={`flex-1 h-1 mx-4 rounded-full transition-all duration-300 ${
                  item.active
                    ? 'bg-gradient-to-r from-indigo-600 to-blue-600'
                    : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Upload Zone */}
        {!isAnalyzing ? (
          <>
            {/* Drop Zone */}
            <div
              ref={dropZoneRef}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative rounded-3xl border-2 border-dashed p-12 mb-8 cursor-pointer transition-all duration-300 ${
                isDragging
                  ? 'border-indigo-500 bg-indigo-50 shadow-xl'
                  : 'border-gray-300 bg-white hover:border-indigo-400 hover:bg-indigo-50/50 hover:shadow-lg'
              }`}
            >
              {/* Animated background */}
              <div className={`absolute inset-0 rounded-3xl overflow-hidden ${isDragging ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-blue-500/5" />
              </div>

              {/* Content */}
              <div className="relative flex flex-col items-center justify-center">
                <div className={`mb-6 p-4 rounded-2xl transition-all duration-300 transform ${
                  isDragging
                    ? 'bg-gradient-to-br from-indigo-600 to-blue-600 shadow-xl scale-110'
                    : 'bg-gradient-to-br from-indigo-100 to-blue-100'
                }`}>
                  <Icons.DocumentIcon className={`w-8 h-8 transition-colors ${
                    isDragging ? 'text-white' : 'text-indigo-600'
                  }`} />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {file ? file.name : 'Drag and drop your document here'}
                </h3>

                {!file ? (
                  <p className="text-gray-600 mb-4">or click to browse for a Word (.docx) file</p>
                ) : (
                  <p className="text-green-600 font-medium mb-4 flex items-center gap-2">
                    <Icons.CheckCircleIcon className="w-5 h-5" />
                    File selected
                  </p>
                )}

                <div className="text-sm text-gray-500">
                  Maximum file size: 50 MB • Supported format: .docx
                </div>

                {/* Hidden input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>

            {/* Journal Selection */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-200/50 mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-3">
                <div className="w-1 h-6 bg-gradient-to-b from-indigo-600 to-blue-600 rounded-full" />
                Select Journal
              </h3>

              {journalAutoDetected && (
                <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-2">
                  <Icons.CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                  Journal auto-detected from filename
                </div>
              )}

              {/* Search box */}
              <div className="relative mb-3">
                <input
                  type="text"
                  placeholder="Search journal by name or code…"
                  value={journalSearch}
                  onChange={e => setJournalSearch(e.target.value)}
                  className="w-full px-4 py-3 pl-10 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm"
                />
                <svg className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
              </div>

              {/* Journal list */}
              <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
                {journals
                  .filter(j =>
                    !journalSearch ||
                    j.code.toLowerCase().includes(journalSearch.toLowerCase()) ||
                    j.name.toLowerCase().includes(journalSearch.toLowerCase())
                  )
                  .map((j) => (
                    <button
                      key={j.code}
                      onClick={() => { setJournal(j.code); setJournalAutoDetected(false); setJournalSearch(''); }}
                      className={`w-full px-4 py-3 flex items-center justify-between text-left transition-colors duration-150 ${
                        journal === j.code
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'bg-white hover:bg-gray-50 text-gray-900'
                      }`}
                    >
                      <div>
                        <span className="font-semibold text-sm">{j.code}</span>
                        <span className="text-gray-500 text-sm ml-2">— {j.name}</span>
                      </div>
                      {journal === j.code && (
                        <Icons.CheckCircleIcon className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                {journals.filter(j =>
                  !journalSearch ||
                  j.code.toLowerCase().includes(journalSearch.toLowerCase()) ||
                  j.name.toLowerCase().includes(journalSearch.toLowerCase())
                ).length === 0 && (
                  <p className="px-4 py-6 text-sm text-gray-500 text-center">No journals match your search</p>
                )}
              </div>

              {journal && (
                <p className="mt-3 text-sm text-indigo-600 font-medium">
                  Selected: {journal} — {journals.find(j => j.code === journal)?.name}
                </p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-8 p-4 rounded-2xl bg-red-50 border border-red-200 flex items-start gap-3 animate-shake">
                <Icons.ExclamationIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-900">{error}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/qc/dashboard')}
                className="flex-1 px-6 py-4 rounded-2xl border-2 border-gray-300 text-gray-900 font-semibold hover:bg-gray-50 transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAnalyze}
                disabled={!file || !journal}
                className={`flex-1 px-6 py-4 rounded-2xl font-semibold text-white shadow-lg transition-all duration-300 transform ${
                  !file || !journal
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:shadow-xl hover:scale-105 active:scale-95'
                }`}
              >
                Start Analysis
              </button>
            </div>
          </>
        ) : (
          /* Analysis Progress */
          <div className="bg-white rounded-3xl p-12 shadow-lg text-center">
            <div className="mb-8">
              <div className="relative inline-block">
                <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    fill="none"
                    stroke="url(#progressGradient)"
                    strokeWidth="8"
                    strokeDasharray={`${progress * 3.4} 340`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                  <defs>
                    <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#4f46e5" />
                      <stop offset="100%" stopColor="#2563eb" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-4xl font-bold text-gray-900">
                  {Math.round(progress)}%
                </span>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-2">Analyzing Document</h3>
            <p className="text-gray-600 mb-8">Running QC checks against {journals.find(j => j.code === journal)?.name || journal}...</p>

            {/* Status Steps */}
            <div className="space-y-3 max-w-md mx-auto">
              {[
                { label: 'Extracting content', progress: 20 },
                { label: 'Checking abbreviations', progress: 40 },
                { label: 'Validating formatting', progress: 60 },
                { label: 'Verifying references', progress: 80 },
                { label: 'Generating report', progress: 100 },
              ].map((step, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                    progress >= step.progress
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {progress >= step.progress ? (
                      <Icons.CheckCircleIcon className="w-5 h-5" />
                    ) : progress >= step.progress - 20 ? (
                      <div className="w-5 h-5 border-2 border-transparent border-t-indigo-600 rounded-full animate-spin" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-gray-300" />
                    )}
                    {step.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default QCUploadPage;
